import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Hourglass, Info, MoreVertical, Star } from 'lucide-react';
import { learnAPI } from '../lib/api';
import { t } from '../lib/i18n';
import { EmptyState } from '../components/ui';
import MyLearningShell from '../components/MyLearningShell';

// My learning → My courses (s134 redesign, Udemy register): frameless 4-up
// cards — thumbnail, bold title, author, thin progress bar, % complete and the
// member's own star rating ("Leave a rating" opens the course page's rating
// dialog). 0% shows START COURSE. The whole card opens the player.

const SORTS = {
  recent: (a, b) => (b.last_accessed || '').localeCompare(a.last_accessed || ''),
  enrolled: (a, b) => (b.enrolled_at || '').localeCompare(a.enrolled_at || ''),
  title: (a, b) => (a.title || '').localeCompare(b.title || ''),
};

function Stars({ value }) {
  return (
    <span className="flex gap-0.5 text-lms-rating" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={13} fill={n <= value ? 'currentColor' : 'none'} />
      ))}
    </span>
  );
}

function CardMenu({ slug }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div ref={ref} className="absolute right-2 top-2 z-10">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t('Course options')}
        aria-expanded={open}
        className="lms-press flex h-8 w-8 items-center justify-center rounded-lg bg-lms-chip-bg text-lms-chip-ink shadow-[var(--lms-elev-1)]"
      >
        <MoreVertical size={16} />
      </button>
      {open && (
        <div className="absolute right-0 top-10 w-44 overflow-hidden rounded-lg border border-lms-line bg-lms-surface py-1 shadow-[var(--lms-elev-3)]">
          <Link to={`/course/${slug}`} className="flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-lms-ink hover:bg-lms-raised">
            <Info size={15} /> {t('Course details')}
          </Link>
          <Link to={`/course/${slug}?rate=1`} className="flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-lms-ink hover:bg-lms-raised">
            <Star size={15} /> {t('Rate this course')}
          </Link>
        </div>
      )}
    </div>
  );
}

function LearningCard({ c }) {
  const pct = c.progress_pct || 0;
  // s135: a registration awaiting approval is listed but opens the course page
  const pending = c.enrollment_status === 'pending';
  const href = pending ? `/course/${c.slug}` : `/learn/${c.slug}`;
  return (
    <div className="relative">
      <Link to={href} className="group flex flex-col gap-2.5">
        {c.thumbnail ? (
          <img src={c.thumbnail} alt="" loading="lazy" className="aspect-video w-full rounded-lg border border-lms-line object-cover" />
        ) : (
          <div className="flex aspect-video w-full items-center justify-center rounded-lg bg-lms-sidebar p-4 text-center">
            <span className="line-clamp-3 text-base font-bold leading-snug text-white">{c.title}</span>
          </div>
        )}
        <h3 className="lms-clamp2 text-base font-bold leading-snug text-lms-ink group-hover:text-lms-link">{c.title}</h3>
        {c.instructor_name && <span className="-mt-1 text-[12.5px] text-lms-sub">{c.instructor_name}</span>}
        <div className="h-1 overflow-hidden rounded-sm bg-lms-line">
          {pct > 0 && <div className="h-full bg-lms-accent" style={{ width: `${pct}%` }} />}
        </div>
      </Link>
      <div className="mt-2.5 flex items-start justify-between gap-2">
        {pending ? (
          <span className="flex items-center gap-1.5 text-[13px] font-semibold text-lms-body">
            <Hourglass size={14} /> {t('Pending approval')}
          </span>
        ) : c.awaiting_release ? (
          <span className="flex flex-col gap-0.5">
            <span className="text-[13px] text-lms-body lms-num">{pct}% {t('complete')}</span>
            <span className="text-xs text-lms-sub">{t('Next lesson coming soon')}</span>
          </span>
        ) : pct > 0 ? (
          <span className="text-[13px] text-lms-body lms-num">{pct}% {t('complete')}</span>
        ) : (
          <Link to={`/learn/${c.slug}`} className="text-[13px] font-bold uppercase tracking-wide text-lms-ink hover:text-lms-link">
            {t('Start course')}
          </Link>
        )}
        {pct > 0 && !pending && (
          <Link to={`/course/${c.slug}?rate=1`} className="flex flex-col items-end gap-0.5 hover:opacity-80">
            <Stars value={c.my_rating || 0} />
            <span className="text-xs text-lms-sub">{c.my_rating ? t('Your rating') : t('Leave a rating')}</span>
          </Link>
        )}
      </div>
      <CardMenu slug={c.slug} />
    </div>
  );
}

export default function MyCourses() {
  const [courses, setCourses] = useState(null);
  const [sort, setSort] = useState('recent');

  useEffect(() => {
    learnAPI.myCourses()
      .then(({ data }) => setCourses(data.courses))
      .catch(() => setCourses([]));
  }, []);

  const sorted = useMemo(() => [...(courses || [])].sort(SORTS[sort]), [courses, sort]);

  if (!courses) {
    return <MyLearningShell><p className="text-sm text-lms-muted">{t('Loading…')}</p></MyLearningShell>;
  }

  return (
    <MyLearningShell>
      {courses.length === 0 ? (
        <EmptyState>
          {t('You are not enrolled in any course yet.')}{' '}
          <Link to="/courses" className="text-lms-link">{t('Browse courses')}</Link>
        </EmptyState>
      ) : (
        <>
          <div className="mb-5 flex items-center justify-between">
            <span className="text-[15px] font-bold text-lms-ink lms-num">
              {courses.length} {courses.length === 1 ? t('course') : t('courses')}
            </span>
            <label className="relative inline-flex h-11 cursor-pointer items-center gap-1.5 px-1 text-sm font-bold text-lms-ink">
              {({ recent: t('Recently accessed'), enrolled: t('Recently enrolled'), title: t('Title A–Z') })[sort]}
              <ChevronDown size={14} />
              <select
                aria-label={t('Sort by')}
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="absolute inset-0 cursor-pointer opacity-0"
              >
                <option value="recent">{t('Recently accessed')}</option>
                <option value="enrolled">{t('Recently enrolled')}</option>
                <option value="title">{t('Title A–Z')}</option>
              </select>
            </label>
          </div>
          <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {sorted.map((c) => <LearningCard key={c.id} c={c} />)}
          </div>
        </>
      )}
    </MyLearningShell>
  );
}
